import React, { useEffect, useState } from "react";
import { chatbotService } from "../../../../api/chatbotService";
import styles from "./asset/PracticeModuleTool.module.scss";

const SystemPromptEditorTab = ({ sessionId, answerConfirmed = false, onCustomPromptStateChange }) => {
    const [editedPrompt, setEditedPrompt] = useState("");
    const [isCustom, setIsCustom] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    useEffect(() => {
        loadCurrentPrompt();
    }, [sessionId, answerConfirmed]);

    useEffect(() => {
        if (onCustomPromptStateChange) {
            onCustomPromptStateChange(isCustom);
        }
    }, [isCustom, onCustomPromptStateChange]);

    const loadCurrentPrompt = async () => {
        setIsLoading(true);
        setMessage({ type: "", text: "" });

        try {
            const response = await chatbotService.getCurrentPrompt(sessionId, answerConfirmed);
            setEditedPrompt(response.prompt || "");
            setIsCustom(Boolean(response.is_custom));
        } catch (error) {
            setMessage({
                type: "error",
                text: `Failed to load prompt: ${error.message}`,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!sessionId) {
            setMessage({
                type: "error",
                text: "No active session. Please start a chat first.",
            });
            return;
        }

        if (!editedPrompt || !editedPrompt.trim()) {
            setMessage({
                type: "error",
                text: "Prompt cannot be empty.",
            });
            return;
        }

        setIsSaving(true);
        setMessage({ type: "", text: "" });

        try {
            await chatbotService.setSessionPrompt(sessionId, editedPrompt, Boolean(answerConfirmed));
            setIsCustom(true);
            setMessage({
                type: "success",
                text: "Custom prompt saved successfully.",
            });
        } catch (error) {
            setMessage({
                type: "error",
                text: `Failed to save prompt: ${error.message}`,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = async () => {
        if (!sessionId) {
            setMessage({
                type: "error",
                text: "No active session.",
            });
            return;
        }

        setIsLoading(true);
        setMessage({ type: "", text: "" });

        try {
            await chatbotService.clearSessionPrompt(sessionId);
            const response = await chatbotService.getDefaultPrompt(answerConfirmed);
            setEditedPrompt(response.prompt || "");
            setIsCustom(false);
            setMessage({
                type: "success",
                text: "Prompt reset to default.",
            });
        } catch (error) {
            setMessage({
                type: "error",
                text: `Failed to reset prompt: ${error.message}`,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewDefault = async () => {
        setIsLoading(true);
        setMessage({ type: "", text: "" });

        try {
            const response = await chatbotService.getDefaultPrompt(answerConfirmed);
            setEditedPrompt(response.prompt || "");
            setMessage({
                type: "info",
                text: "Viewing default prompt. Save to apply it to this session.",
            });
        } catch (error) {
            setMessage({
                type: "error",
                text: `Failed to load default prompt: ${error.message}`,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.systemPromptTab}>
            <div className={styles.badgeRow}>
                <span className={`${styles.badge} ${isCustom ? styles.badgeCustom : styles.badgeDefault}`}>
                    {isCustom ? "Custom Prompt" : "Default Prompt"}
                </span>
                <span className={`${styles.badge} ${answerConfirmed ? styles.badgeConfirmed : styles.badgeUnconfirmed}`}>
                    {answerConfirmed ? "Answer Confirmed" : "Not Confirmed"}
                </span>
            </div>

            {message.text && (
                <div className={`${styles.alert} ${styles[`alert${message.type.charAt(0).toUpperCase()}${message.type.slice(1)}`]}`}>
                    {message.text}
                </div>
            )}

            {isLoading ? (
                <div className={styles.loadingBox}>Loading prompt...</div>
            ) : (
                <textarea
                    className={styles.promptTextarea}
                    value={editedPrompt}
                    onChange={(event) => setEditedPrompt(event.target.value)}
                    placeholder="Enter system prompt"
                    rows={16}
                    wrap="soft"
                />
            )}

            <div className={styles.actionButtons}>
                <button
                    className={styles.btnPrimary}
                    onClick={handleSave}
                    type="button"
                    disabled={isSaving || isLoading || !editedPrompt.trim()}
                >
                    {isSaving ? "Saving..." : "Save Custom Prompt"}
                </button>
                <button
                    className={styles.btnSecondary}
                    onClick={handleViewDefault}
                    type="button"
                    disabled={isLoading}
                >
                    View Default
                </button>
                <button
                    className={styles.btnWarning}
                    onClick={handleReset}
                    type="button"
                    disabled={isLoading || !isCustom}
                >
                    Reset To Default
                </button>
            </div>

            <div className={styles.helperCard}>
                Changes are session scoped. They only affect this chat context and keep the existing feature-flag behavior.
            </div>
        </div>
    );
};

export default SystemPromptEditorTab;
